// src/Components/GoogleSlide/GoogleSlide.jsx

import React from "react";
import { Box, Typography, Button, useMediaQuery } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import Grid from "@mui/material/Grid";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import "swiper/css";
import "swiper/css/navigation";

import StarIcon from "@mui/icons-material/Star";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import useGoogleReviews from "../../hooks/useGoogleReviews";
import Logo from "../../Assets/logo.png";

const renderStars = (rating, size = 16) => {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  const total = full + (half ? 1 : 0);
  return (
    <Box sx={{ display: "flex", gap: "2px" }}>
      {[...Array(full)].map((_, i) => (
        <StarIcon key={`f-${i}`} sx={{ color: "#FFD700", fontSize: size }} />
      ))}
      {half && <StarHalfIcon sx={{ color: "#FFD700", fontSize: size }} />}
      {[...Array(5 - total)].map((_, i) => (
        <StarBorderIcon key={`e-${i}`} sx={{ color: "#FFD700", fontSize: size }} />
      ))}
    </Box>
  );
};

export default function ReviewSection() {
  // --- All hooks are now called at the top level ---
  const isSmall = useMediaQuery((t) => t.breakpoints.down("sm"));
  const isMedium = useMediaQuery((t) => t.breakpoints.down("md"));
  const { reviews, rating, total, loading } = useGoogleReviews();

  // Changed to process the 5 newest reviews from the API.
  const latestFiveStarReviews = React.useMemo(() => {
    return (reviews || [])
      .sort((a, b) => b.time - a.time) // Sort by most recent first
      .slice(0, 5);                   // Take up to 5 newest reviews
  }, [reviews]);

  // --- Conditional returns can now happen after all hooks have been called ---
  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center", background: "#141414", color: "#fff" }}>
        <Typography>Loading reviews…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ background: "#141414", color: "#fff", py: 5, textAlign: "center" }}>
      <Box sx={{ maxWidth: 1300, mx: "auto", py: 3 }}>
        <Grid container alignItems="center">
          <Grid item lg={3} md={3} sm={12} xs={12}>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                px: 2,
                mb: { xs: 4, sm: 4, md: 0 }
              }}
            >
              <Typography variant="h5" fontWeight="bold" sx={{ color: "#fff", fontSize: 24 }}>
                {rating ? rating.toFixed(1) : "–"}
              </Typography>
              {renderStars(rating)}
              <Typography variant="body1" sx={{ color: "#fff", mt: 1 }}>
                Based on <strong>{total}</strong> reviews
              </Typography>
              <Typography variant="caption" sx={{ color: "#bbb", mt: 1 }}>
                Powered by Google
              </Typography>
            </Box>
          </Grid>

          <Grid item lg={9} md={9} sm={12} xs={12}>
            <Swiper
              modules={[Navigation]}
              navigation
              spaceBetween={40}
              slidesPerView={isSmall ? 1 : isMedium ? 2 : 3}
              style={{ padding: "10px 40px" }}
            >
              {latestFiveStarReviews.map((r) => {
                return (
                  <SwiperSlide key={r.author_url} style={{ display: "flex" }}>
                    <a
                      href={r.author_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none", width: "90%" }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "#D4F1F4",
                          border: "2px solid #00AAE3",
                          borderRadius: "20px",
                          p: 3,
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                          height: 280,
                          overflow: "hidden",
                          transition: "box-shadow 0.2s ease",
                          "&:hover": {
                            boxShadow: "0 6px 12px rgba(0,0,0,0.15)",
                          },
                        }}
                      >
                        <Box sx={{ mb: 2, minHeight: 80 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <img
                              src={Logo}
                              alt="logo"
                              loading="lazy"
                              style={{ width: 50, height: 50, borderRadius: "50%" }}
                            />
                            <Box sx={{ textAlign: "left" }}>
                              <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#000" }}>
                                {r.author_name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {new Date(r.time * 1000).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ mt: 1 }}>{renderStars(r.rating, 18)}</Box>
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{
                            flexGrow: 1,
                            color: "#333",
                            lineHeight: 1.5,
                            textAlign: "left",
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 7,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {r.text}
                        </Typography>
                      </Box>
                    </a>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </Grid>
        </Grid>
      </Box>

      <Button
        component="a"
        href="https://g.page/r/CVBxrRaQf5rCEAE/review"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          mt: 3,
          backgroundColor: "#00AAE3",
          color: "#fff",
          textTransform: "none",
          borderRadius: "30px",
          px: 4,
          py: 1.5,
          fontSize: 16,
          "&:hover": { backgroundColor: "#33BBE8" },
        }}
      >
        Leave A Google Review <ChevronRightIcon sx={{ ml: 1 }} />
      </Button>
    </Box>
  );
}